import { useEffect, useState, useCallback } from "react";
import client, { apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChevronLeft, ChevronRight, Loader2, Palmtree } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];
const MNAME = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const num = (v) => (v === "" || v === null || v === undefined || isNaN(Number(v)) ? null : Number(v));
const fmt = (v, dp = 2) => (v === null || v === undefined ? "—" : String(Math.round(v * 10 ** dp) / 10 ** dp));

// Mirrors backend holiday_pay.summarise: pro-rata by days left in the year, allowance days to 1 dp.
function summarise(f, year) {
  const ah = num(f.allowanceHours), hpd = num(f.hoursPerDay);
  let factor = 1, first = 1;
  if (f.startDate) {
    const s = new Date(f.startDate + "T00:00:00Z");
    if (s.getUTCFullYear() > year) { factor = 0; first = 13; }
    else if (s.getUTCFullYear() === year) {
      const end = Date.UTC(year, 11, 31), startY = Date.UTC(year, 0, 1);
      factor = ((end - s.getTime()) / 864e5 + 1) / ((end - startY) / 864e5 + 1);
      first = s.getUTCMonth() + 1;
    }
  }
  const allowH = ah !== null ? Math.round(ah * factor * 100) / 100 : null;
  const allowD = allowH !== null && hpd ? Math.round((allowH / hpd) * 10) / 10 : null;
  let taken = 0;
  const rows = MONTHS.map((m, i) => {
    const e = f.months[m] || {};
    let d = num(e.days), h = num(e.hours);
    if (d === null && h !== null && hpd) d = h / hpd;
    if (h === null && d !== null && hpd) h = d * hpd;
    taken += d || 0;
    return { m, i, active: i + 1 >= first, d, h, rem: allowD !== null ? allowD - taken : null };
  });
  return { factor, allowH, allowD, taken, rows, hpd };
}

export function HolidayPay({ userId }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setF(null);
    try {
      const { data } = await client.get(`/staff/${userId}/holiday`, { params: { year } });
      setF({ allowanceHours: data.allowanceHours ?? "", hoursPerDay: data.hoursPerDay ?? "", startDate: data.startDate || "", months: data.months || {} });
    } catch (e) { toast.error(apiErr(e)); }
  }, [userId, year]);
  useEffect(() => { load(); }, [load]);

  if (!f) return <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  const s = summarise(f, year);
  // Typing days clears hours (and vice versa) so the other is worked out from hours per day.
  const setMonth = (m, key, v) => setF({ ...f, months: { ...f.months, [m]: { [key]: v } } });
  const shown = (r, key) => {
    const e = f.months[r.m] || {};
    if (e[key] !== undefined && e[key] !== null && e[key] !== "") return e[key];
    const v = key === "days" ? r.d : r.h;
    return v === null ? "" : String(Math.round(v * 100) / 100);
  };
  const save = async () => {
    setSaving(true);
    try {
      const months = {};
      Object.entries(f.months).forEach(([m, e]) => { if (num(e.days) !== null || num(e.hours) !== null) months[m] = { days: num(e.days), hours: num(e.hours) }; });
      await client.put(`/staff/${userId}/holiday`, { allowanceHours: num(f.allowanceHours), hoursPerDay: num(f.hoursPerDay), startDate: f.startDate || "", months }, { params: { year } });
      toast.success("Holiday pay saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <Card className="p-5 space-y-5" data-testid="sp-holiday">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-semibold flex items-center gap-2"><Palmtree className="h-4 w-4 text-brand" />Holiday pay</h3>
        <div className="flex items-center gap-1">
          <Button size="icon" variant="ghost" onClick={() => setYear(year - 1)} data-testid="hp-year-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="font-semibold w-32 text-center whitespace-nowrap" data-testid="hp-year">Jan–Dec {year}</span>
          <Button size="icon" variant="ghost" onClick={() => setYear(year + 1)} data-testid="hp-year-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <div><Label>Total allowance (hours per full year)</Label>
          <Input type="number" min="0" step="0.5" value={f.allowanceHours} onChange={(e) => setF({ ...f, allowanceHours: e.target.value })} data-testid="hp-allowance-hours" /></div>
        <div><Label>Hours per day</Label>
          <Input type="number" min="0" step="0.25" value={f.hoursPerDay} onChange={(e) => setF({ ...f, hoursPerDay: e.target.value })} data-testid="hp-hours-per-day" /></div>
        <div><Label>Holiday calculation start day</Label>
          <Input type="date" value={f.startDate} onChange={(e) => setF({ ...f, startDate: e.target.value })} data-testid="hp-start-date" />
          <p className="text-[11px] text-slate-400 mt-1">Leave blank for a full year. Mid-year starts are pro-rata.</p></div>
      </div>
      <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-sm flex flex-wrap gap-x-6 gap-y-1" data-testid="hp-summary">
        <span>Allowance this year: <b data-testid="hp-allowance-days">{fmt(s.allowD, 1)} days</b>{s.allowH !== null && <span className="text-slate-500"> ({fmt(s.allowH)}h)</span>}</span>
        {s.factor < 1 && <span className="text-slate-500" data-testid="hp-prorata">Pro-rata {Math.round(s.factor * 1000) / 10}% of the year</span>}
        <span>Taken: <b data-testid="hp-taken-days">{fmt(s.taken)} days</b></span>
        <span>Remaining: <b className={s.allowD !== null && s.allowD - s.taken < 0 ? "text-red-600" : "text-emerald-700"} data-testid="hp-remaining-days">{s.allowD !== null ? `${fmt(s.allowD - s.taken)} days` : "—"}</b></span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b">
            <th className="py-2 pr-3">Month</th><th className="py-2 pr-3">Days taken</th><th className="py-2 pr-3">Hours taken</th><th className="py-2">Remaining (days)</th>
          </tr></thead>
          <tbody>
            {s.rows.map((r) => (
              <tr key={r.m} className={`border-b border-slate-100 ${r.active ? "" : "opacity-40"}`} data-testid={`hp-row-${r.m}`}>
                <td className="py-1.5 pr-3 font-medium">{MNAME[r.i]}</td>
                <td className="py-1.5 pr-3"><Input type="number" min="0" step="0.5" className="h-8 w-24" disabled={!r.active} value={shown(r, "days")} onChange={(e) => setMonth(r.m, "days", e.target.value)} data-testid={`hp-days-${r.m}`} /></td>
                <td className="py-1.5 pr-3"><Input type="number" min="0" step="0.25" className="h-8 w-24" disabled={!r.active} value={shown(r, "hours")} onChange={(e) => setMonth(r.m, "hours", e.target.value)} data-testid={`hp-hours-${r.m}`} /></td>
                <td className={`py-1.5 font-medium ${r.rem !== null && r.rem < 0 ? "text-red-600" : ""}`} data-testid={`hp-remaining-${r.m}`}>
                  {r.active && r.rem !== null ? <>{fmt(r.rem)}{s.hpd ? <span className="text-slate-400 font-normal"> ({fmt(r.rem * s.hpd)}h)</span> : null}</> : "—"}
                </td>
              </tr>
            ))}
            <tr className="font-semibold" data-testid="hp-total-row"><td className="py-2 pr-3">Total</td><td className="py-2 pr-3">{fmt(s.taken)}</td><td className="py-2 pr-3">{s.hpd ? fmt(s.taken * s.hpd) : "—"}</td><td className="py-2">{s.allowD !== null ? fmt(s.allowD - s.taken) : "—"}</td></tr>
          </tbody>
        </table>
      </div>
      <div className="flex justify-end"><Button onClick={save} disabled={saving} data-testid="hp-save">{saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Save</Button></div>
    </Card>
  );
}
